import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Typography,
  Button,
  Box,
  Paper,
  Stack,
  Divider,
  Alert,
  CircularProgress,
} from "@mui/material";
import { API } from "../../../config";
import CourierLayout from "../CourierLayout";

const ProofOfDeliveryViewer = () => {
  const { orderId } = useParams();
  const history = useHistory();

  const [pod, setPod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const getPhotoUrl = (file) => {
    const raw = file?.url || file?.path || file;
    if (!raw) return "";
    if (String(raw).startsWith("http")) return raw;
    return `${API}${String(raw).startsWith("/") ? raw : `/${raw}`}`;
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchLoadCompletion = async () => {
      try {
        setLoading(true);
        setErr("");

        const token =
          localStorage.getItem("courier_token") ||
          localStorage.getItem("token");

        const response = await fetch(`${API}/api/proof-of-delivery/${orderId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.message || `Unable to load completion records (${response.status})`);
        }

        setPod(data);
      } catch (error) {
        console.error("Error loading completion records:", error);
        setErr(error.message || "Unable to load completion records.");
      } finally {
        setLoading(false);
      }
    };

    fetchLoadCompletion();
  }, [orderId]);

  const files =
    pod?.files ||
    pod?.photo_paths ||
    pod?.photos ||
    pod?.photoUrls ||
    pod?.photo_urls ||
    [];

  const photos = Array.isArray(files) ? files : files ? [files] : [];

  return (
    <CourierLayout>
      <Box sx={{ width: "100%", overflowX: "hidden" }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.25, sm: 3, md: 4 },
            borderRadius: 3,
            maxWidth: 900,
            mx: "auto",
            border: "1px solid #e2e8f0",
            boxShadow: {
              xs: "none",
              md: "0 10px 24px rgba(15, 23, 42, 0.06)",
            },
          }}
        >
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Typography
              variant="h4"
              fontWeight={800}
              gutterBottom
              sx={{ fontSize: { xs: "2rem", md: "2.5rem" } }}
            >
              Load Completion
            </Typography>

            <Typography variant="body1" color="text.secondary">
              View carrier-uploaded photos, notes, signatures, and load completion records.
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Load #{orderId}
            </Typography>
          </Box>

          {err && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {err}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : !pod ? (
            <Alert severity="info">
              Load completion records are not available yet.
            </Alert>
          ) : (
            <Stack spacing={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  backgroundColor: "#fff",
                }}
              >
                <Typography variant="h6" fontWeight={800} gutterBottom>
                  Load Completion Details
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Stack spacing={1.5}>
                  <Typography>
                    <strong>Completed At:</strong>{" "}
                    {pod?.created_at
                      ? new Date(pod.created_at).toLocaleString()
                      : "Not available"}
                  </Typography>

                  <Typography>
                    <strong>Carrier Notes:</strong>{" "}
                    {pod?.delivery_notes || pod?.notes || "No notes provided."}
                  </Typography>

                  <Typography>
                    <strong>Recipient Signature:</strong>{" "}
                    {pod?.signature || "No signature provided."}
                  </Typography>
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  backgroundColor: "#fff",
                }}
              >
                <Typography variant="h6" fontWeight={800} gutterBottom>
                  Completion Photos
                </Typography>

                <Divider sx={{ mb: 2 }} />

                {photos.length === 0 ? (
                  <Alert severity="info">
                    No completion photos were uploaded.
                  </Alert>
                ) : (
                  <Stack spacing={2}>
                    {photos.map((photo, index) => (
                      <Box
                        key={`${getPhotoUrl(photo)}-${index}`}
                        component="img"
                        src={getPhotoUrl(photo)}
                        alt={`Load completion ${index + 1}`}
                        sx={{
                          width: "100%",
                          maxHeight: 520,
                          objectFit: "contain",
                          borderRadius: 2,
                          border: "1px solid #e2e8f0",
                          backgroundColor: "#f8fafc",
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Paper>
            </Stack>
          )}

          <Button
            variant="contained"
            fullWidth
            onClick={() => history.goBack()}
            sx={{
              mt: 3,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
            }}
          >
            Back
          </Button>
        </Paper>
      </Box>
    </CourierLayout>
  );
};

export default ProofOfDeliveryViewer;